import React, { useEffect, useState } from 'react';
import { groupKey, loadTwoStepStatus, replaceRecoveryCodes, startTwoStep, turnOffTwoStep, verifyTwoStep } from '../adminTwoStep';
function getQRCodeSVG() {
    try {
        return require('qrcode.react').QRCodeSVG;
    }
    catch {
        return null;
    }
}
const QRCodeSVG = getQRCodeSVG();
const AdminTwoStepPanel = ({ adminToken, base, fetchImpl, initialView, onClose, onSession, themeClass, ticket }) => {
    const [view, setView] = useState(initialView || { kind: 'loading' });
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const call = fetchImpl || ((input, init) => fetch(input, init));
    const auth = ticket ? { ticket } : { token: adminToken || '' };
    const showStatus = async () => {
        if (!adminToken)
            return;
        const status = await loadTwoStepStatus(call, base, adminToken);
        if (!status.ok) {
            setError(status.error);
            return;
        }
        setView({ enabled: status.enabled, kind: 'status', remaining: status.remainingRecoveryCodes, required: status.required });
    };
    const begin = async (withPassword) => {
        const started = await startTwoStep(call, base, auth, withPassword);
        if (!started.ok) {
            setError(started.error);
            return;
        }
        setCode('');
        setView({ kind: 'scan', otpauthUri: started.otpauthUri, secret: started.secret });
    };
    useEffect(() => {
        if (initialView)
            return;
        void (ticket ? begin() : showStatus());
    }, []);
    const run = async (work) => {
        if (busy)
            return;
        setBusy(true);
        setError('');
        try {
            await work();
        }
        finally {
            setBusy(false);
        }
    };
    const onConfirm = (event) => {
        event.preventDefault();
        if (view.kind !== 'confirm')
            return;
        const { action } = view;
        void run(async () => {
            if (action === 'setup') {
                await begin(password);
            }
            else if (action === 'regenerate') {
                const replaced = await replaceRecoveryCodes(call, base, adminToken || '', password, code);
                if (!replaced.ok) {
                    setError(replaced.error);
                    return;
                }
                setView({ kind: 'codes', recoveryCodes: replaced.recoveryCodes, session: null });
            }
            else {
                const off = await turnOffTwoStep(call, base, adminToken || '', password, code);
                if (!off.ok) {
                    setError(off.error);
                    return;
                }
                await showStatus();
            }
            setPassword('');
            setCode('');
        });
    };
    const onVerify = (event) => {
        event.preventDefault();
        void run(async () => {
            const verified = await verifyTwoStep(call, base, auth, code);
            if (!verified.ok) {
                setError(verified.error);
                return;
            }
            setCode('');
            setView({ kind: 'codes', recoveryCodes: verified.recoveryCodes, session: verified.session });
        });
    };
    const onDone = () => {
        if (view.kind === 'codes' && view.session) {
            onSession?.(view.session);
            return;
        }
        void run(showStatus);
    };
    const ask = (action) => {
        setError('');
        setPassword('');
        setCode('');
        setView({ action, kind: 'confirm' });
    };
    const back = (React.createElement("button", { className: 'vrs-password-toggle', disabled: busy, onClick: onClose, type: 'button' }, "Back"));
    return (React.createElement("div", { "aria-labelledby": 'vrs-two-step-title', className: 'vrs-auth-form vrs-two-step', role: 'group' },
        React.createElement("p", { className: 'vrs-auth-subtitle', id: 'vrs-two-step-title' }, "Two-step sign-in"),
        error ? React.createElement("div", { className: 'vrs-auth-error' }, error) : null,
        view.kind === 'loading' ? React.createElement("p", { className: 'vrs-two-step-state' }, "Loading\u2026") : null,
        view.kind === 'status' ? (React.createElement(React.Fragment, null,
            React.createElement("p", { className: 'vrs-two-step-state' }, view.enabled ? `On · ${view.remaining} recovery codes left` : 'Off'),
            view.enabled ? (React.createElement(React.Fragment, null,
                React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, onClick: () => ask('regenerate'), type: 'button' }, "New recovery codes"),
                view.required ? null : (React.createElement("button", { className: 'vrs-password-toggle', onClick: () => ask('disable'), type: 'button' }, "Turn off")))) : (React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, onClick: () => ask('setup'), type: 'button' }, "Set up")),
            back)) : null,
        view.kind === 'confirm' ? (React.createElement("form", { onSubmit: onConfirm },
            React.createElement("div", { className: 'vrs-auth-field' },
                React.createElement("label", { htmlFor: 'vrs-two-step-password' }, "Password"),
                React.createElement("input", { autoComplete: 'current-password', autoFocus: true, disabled: busy, id: 'vrs-two-step-password', onChange: event => setPassword(event.target.value), required: true, type: 'password', value: password })),
            view.action === 'setup' ? null : (React.createElement("div", { className: 'vrs-auth-field' },
                React.createElement("label", { htmlFor: 'vrs-two-step-current-code' }, "Code or recovery code"),
                React.createElement("input", { autoComplete: 'one-time-code', disabled: busy, id: 'vrs-two-step-current-code', onChange: event => setCode(event.target.value), required: true, type: 'text', value: code }))),
            React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, disabled: busy, type: 'submit' }, view.action === 'setup' ? 'Continue' : view.action === 'disable' ? 'Turn off' : 'New recovery codes'),
            React.createElement("button", { className: 'vrs-password-toggle', disabled: busy, onClick: () => void run(showStatus), type: 'button' }, "Back"))) : null,
        view.kind === 'scan' ? (React.createElement("form", { onSubmit: onVerify },
            React.createElement("div", { className: 'vrs-two-step-qr' }, QRCodeSVG
                ? React.createElement(QRCodeSVG, { size: 184, title: 'QR code for the authenticator app', value: view.otpauthUri })
                : null),
            React.createElement("p", { className: 'vrs-two-step-key' },
                React.createElement("span", null, "Key"),
                React.createElement("code", null, groupKey(view.secret))),
            React.createElement("div", { className: 'vrs-auth-field' },
                React.createElement("label", { htmlFor: 'vrs-two-step-code' }, "Code"),
                React.createElement("input", { autoComplete: 'one-time-code', autoFocus: true, disabled: busy, id: 'vrs-two-step-code', inputMode: 'numeric', onChange: event => setCode(event.target.value), placeholder: '000000', required: true, type: 'text', value: code })),
            React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, disabled: busy, type: 'submit' }, busy ? 'Verifying...' : 'Verify'),
            back)) : null,
        view.kind === 'codes' ? (React.createElement(React.Fragment, null,
            React.createElement("p", { className: 'vrs-two-step-state' }, "Recovery codes"),
            React.createElement("ul", { className: 'vrs-two-step-codes' }, view.recoveryCodes.map(item => React.createElement("li", { key: item },
                React.createElement("code", null, item)))),
            React.createElement("p", { className: 'vrs-two-step-state' }, "Each code works one time."),
            React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, disabled: busy, onClick: onDone, type: 'button' }, "Done"))) : null));
};
export default AdminTwoStepPanel;
